import { Trans, useLingui } from "@lingui/react/macro";
import { cloneDeep } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { RequestError } from "../../../api/api-connector";
import { NameFields, NameFieldsModel } from "../../../components/name-fields";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TagsInput } from "../../../components/ui-kit/fields/tags-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormField } from "../../../components/ui-kit/form/form-field";
import { FormGrid } from "../../../components/ui-kit/form/form-grid";
import { managingRoles } from "../../../constants/constants";
import { splitNameArray } from "../../../utils/names";
import { createPatch } from "../../../utils/patch";
import { physicianName } from "../../../utils/physician";
import { maybeAsArray } from "../../../utils/utils";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PatientsModal } from "../common/patients-modal";
import { UsersModal } from "../common/users-modal";
import { PhysiciansItem } from "./physicians";

export const PhysicianEditDialog = observer(function PhysicianEditDialog({
  item,
  admin,
  onClose,
  onSave,
}: {
  item: PhysiciansItem;
  admin: AdminConsole;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const { alert } = useDialog();
  const { t } = useLingui();
  const [original] = useState(() => createInitialOriginal(item));
  const [draft] = useState(() =>
    observable<NameFieldsModel & PhysicianEditDraft>(createInitialDraft(item)),
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      className="max-h-[90dvh] w-full sm:max-w-2xl"
    >
      <h3 className="mb-6 text-base font-bold">
        <Trans>Edit physician</Trans>
      </h3>

      <Form
        className="flex flex-col flex-1 min-h-0"
        onSubmit={async () => {
          const patch = createPatch(
            {
              ...draft,
              middle_names: splitNameArray(draft.middle_names),
              additional_family_names: splitNameArray(
                draft.additional_family_names,
              ),
            },
            original,
          );

          if (Object.keys(patch).length === 0) {
            onClose();
            return;
          }

          try {
            await onSave(patch);
            onClose();
          } catch (error) {
            if (
              error instanceof RequestError &&
              typeof error.data === "object" &&
              error.data !== null &&
              "detail" in error.data
            ) {
              await alert(String(error.data.detail));
              return;
            }

            await alert(t`Failed to save physician changes.`);
          }
        }}
      >
        <div className="overflow-y-auto min-h-0 px-1 py-1 flex flex-col gap-4">
          <NameFields model={draft} />

          <FormGrid>
            <FormField label={t`Country`}>
              <CountrySelect attr="country" regionAttr="region" model={draft} />
            </FormField>

            <FormField label={t`Region`}>
              <RegionSelect
                attr="region"
                country={draft.country ?? undefined}
                model={draft}
              />
            </FormField>

            <FormField label={t`Email`}>
              <TextInput attr="email" model={draft} />
            </FormField>

            <FormField label={t`Phone(s)`}>
              <TagsInput attr="phone" model={draft} />
            </FormField>
          </FormGrid>

          <FormField label={t`Bypass geography checks`}>
            <div className="flex gap-4">
              <Checkbox
                label={t`Country`}
                value={draft.bypass_geography_checks[0]}
                onChange={(newValue) => {
                  draft.bypass_geography_checks[0] = newValue;
                  if (newValue) {
                    draft.bypass_geography_checks[1] = true;
                  }
                }}
              />
              <Checkbox
                label={t`Region`}
                value={draft.bypass_geography_checks[1]}
                onChange={(newValue) => {
                  draft.bypass_geography_checks[1] = newValue;
                  if (!newValue) {
                    draft.bypass_geography_checks[0] = false;
                  }
                }}
              />
            </div>
          </FormField>

          <div className="border-border border-t" />

          <div className="flex gap-6 *:flex-1">
            <FormField label={t`Connected user`}>
              <ChooseField
                label={
                  draft.connected_user_uuid
                    ? admin.users?.userMapping[draft.connected_user_uuid]
                    : t`None`
                }
                modal={({ onClose: closeModal }) => {
                  const users = admin.createUsers();
                  return (
                    <UsersModal
                      title={t`Change connected user for physician: ${physicianName(item)}`}
                      preselected={
                        maybeAsArray(draft.connected_user_uuid) ?? undefined
                      }
                      filter={{ role: ["study-physician", "physician"] }}
                      mode="single"
                      model={users}
                      onClose={closeModal}
                      onConfirm={({ selected }) => {
                        draft.connected_user_uuid = selected[0] ?? null;
                      }}
                    />
                  );
                }}
              />
            </FormField>

            <FormField label={t`Clinics`}>
              <ChooseField
                count={draft.clinics?.length ?? 0}
                modal={({ onClose: closeModal }) => {
                  const clinics = admin.createClinics();
                  return (
                    <ClinicsModal
                      preselected={draft.clinics}
                      filter={{ country: draft.country ? [draft.country] : [] }}
                      mode="multi"
                      model={clinics}
                      title={t`Change clinics for physician: ${physicianName(item)}`}
                      onClose={closeModal}
                      onConfirm={({ selected }) => {
                        draft.clinics = selected;
                      }}
                    />
                  );
                }}
              />
            </FormField>

            <FormField label={t`Monitoring users`}>
              <ChooseField
                count={draft.assigned_users?.length ?? 0}
                modal={({ onClose: closeModal }) => {
                  const users = admin.createMonitoringUsers(
                    draft.bypass_geography_checks[0]
                      ? undefined
                      : (draft.country ?? undefined),
                    draft.bypass_geography_checks[1] ? undefined : draft.region,
                  );
                  return (
                    <UsersModal
                      title={t`Change monitoring users for physician: ${physicianName(item)}`}
                      preselected={draft.assigned_users}
                      filter={{ role: managingRoles }}
                      mode="multi"
                      model={users}
                      onClose={closeModal}
                      onConfirm={({ selected }) => {
                        draft.assigned_users = selected;
                      }}
                    />
                  );
                }}
              />
            </FormField>

            <FormField label={t`Patients`}>
              <ChooseField
                count={draft.patients?.length ?? 0}
                modal={({ onClose: closeModal }) => {
                  const patients = admin.createPatients();
                  return (
                    <PatientsModal
                      title={t`Change patients for physician: ${physicianName(item)}`}
                      model={patients}
                      preselected={draft.patients}
                      filter={{ country: draft.country ? [draft.country] : [] }}
                      mode="multi"
                      onClose={closeModal}
                      onConfirm={({ selected }) => {
                        draft.patients = selected;
                      }}
                    />
                  );
                }}
              />
            </FormField>
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit">
            <Trans>Save</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});

type PhysicianEditOriginal = {
  given_name: string | null;
  family_name: string | null;
  middle_names: string[] | null;
  name_particle: string | null;
  additional_family_names: string[] | null;
  name_suffix: string | null;
  title_before: string | null;
  title_after: string | null;
  name_order_given_first: boolean;
  country: string | null;
  region: number | undefined;
  email: string | null;
  phone: string[];
  bypass_geography_checks: [boolean, boolean];
  connected_user_uuid: string | null;
  assigned_users: string[];
  patients: number[];
  clinics: string[];
};

type PhysicianEditDraft = Omit<
  PhysicianEditOriginal,
  "middle_names" | "additional_family_names"
> & {
  middle_names: string | null;
  additional_family_names: string | null;
};

const createInitialOriginal = (item: PhysiciansItem): PhysicianEditOriginal => {
  return {
    given_name: item.given_name ?? null,
    family_name: item.family_name ?? null,
    middle_names: item.middle_names?.length
      ? cloneDeep(item.middle_names)
      : null,
    name_particle: item.name_particle ?? null,
    additional_family_names: item.additional_family_names?.length
      ? cloneDeep(item.additional_family_names)
      : null,
    name_suffix: item.name_suffix ?? null,
    title_before: item.title_before ?? null,
    title_after: item.title_after ?? null,
    name_order_given_first: item.name_order_given_first ?? true,
    country: item.country ?? null,
    region: item.region,
    email: item.email ?? null,
    phone: cloneDeep(item.phone ?? []),
    bypass_geography_checks: cloneDeep(
      item.bypass_geography_checks ?? [false, false],
    ),
    connected_user_uuid: item.connected_user?.uuid ?? null,
    assigned_users: item.assigned_monitoring_users?.map((u) => u.uuid) ?? [],
    patients: item.patients?.map((p) => p.patient_id) ?? [],
    clinics: item.clinics?.map((c) => c.uuid) ?? [],
  };
};

const createInitialDraft = (item: PhysiciansItem) => {
  const original = createInitialOriginal(item);

  return {
    ...cloneDeep(original),
    middle_names: original.middle_names?.join(" ") ?? null,
    additional_family_names:
      original.additional_family_names?.join(" ") ?? null,
  } satisfies PhysicianEditDraft;
};
